<template>
  <div>
    <div class="relativeDiv" :style="`${heightWidthStyle} position:relative;`">
      <div class="containerDiv" :style="containerDivStyle">
        <div v-for="i in elementCount" :key="i" class="d-flex flex-row">
          <HeatMapElement
            v-for="j in elementCount"
            :key="j"
            :size="resolution"
            :color="heatmapColor(powerForLocation(i, j))"
            :power="powerForLocation(i, j).toFixed(1)"
            :percent="getPercent(i, j).toFixed(2)"
          ></HeatMapElement>
        </div>
      </div>

      <div
        v-if="type == 'NaFl'"
        class="roundThing edgeLift"
        :style="edgeLiftStyle"
      ></div>
      <div
        v-else
        class="roundThing innerShadow"
        :style="heightWidthStyle"
      ></div>
      <div class="roundThing outerShadow" :style="heightWidthStyle"></div>
      <div class="ovalHighlight roundThing" :style="ovalHighlightStyle"></div>
      <div
        v-if="type == 'NaFl'"
        class="roundThing bubble"
        :style="bubbleStyle"
      ></div>
    </div>
  </div>
</template>

<script>
import HeatMapElement from "./HeatMapElement.vue";
// import StyleUtilities from "@/assets/js/StyleUtilities";
import RxMethods from "@/assets/js/cls/RxMethods";

// import Name from "../dir/file";

export default {
  name: "HeatMap",
  mixins: [
    // StyleUtilities,
    RxMethods,
  ],
  props: {
    size: {
      type: Number,
      default: 800,
    },
    resolution: {
      type: Number,
      default: 5,
    },
    kObj: null, // a fullkeratometry object
    rxObj: null, // a spec/cl Rx or a tear lens RxObject (used for NaFl films )
    type: {
      type: String,
      default: "keratometry",
      options: ["keratometry", "NaFl", "rx"],
    },
  },
  components: {
    HeatMapElement,
  },
  data: function () {
    return {};
  },
  methods: {
    getAngle(xNum, yNum) {
      let x = xNum - this.middleElement;
      let y = yNum - this.middleElement;
      let angle = Math.atan2(-y, -x);
      angle = angle * (180 / Math.PI);
      angle = angle + 90;
      while (angle < 0) angle += 180;
      while (angle > 180) angle -= 180;
      return angle;
    },
    getPercent(xNum, yNum) {
      let x = xNum - this.middleElement;
      let y = yNum - this.middleElement;
      let rtn = Math.sqrt(x * x + y * y) / (this.middleElement - 1);
      return Math.min(rtn, 1);
    },

    powerForLocation(xNum, yNum) {
      if (this.type == "NaFl") return this.powerForLocationNaFl(xNum, yNum);
      if (this.type == "rx") return this.powerForLocationRx(xNum, yNum);
      if (this.type == "keratometry")
        return this.powerForLocationKeratometry(xNum, yNum);
      return null;
    },

    powerForLocationKeratometry(xNum, yNum) {
      let angle = parseInt(this.getAngle(xNum, yNum));
      let percent = this.getPercent(xNum, yNum);
      let meridianPower = this.meridianPowers[angle];
      let power = meridianPower;
      if (percent < 0.5) {
        power += (0.5 - percent) * 2;
      }
      if (this.kObj && this.cornealCyl(this.kObj) > 0.25) {
        let fromMidK = power - this.midK(this.kObj);
        power += fromMidK;
      }
      return power;
    },
    powerForLocationNaFl(xNum, yNum) {
      let angle = parseInt(this.getAngle(xNum, yNum));
      let percent = this.getPercent(xNum, yNum);
      let meridianPower = this.meridianPowers[angle];
      if (percent == 0) meridianPower = this.rxSphEquiv;
      let pwrAbs = Math.abs(meridianPower);
      let exFactor = meridianPower > 0 ? 2.5 : 1.3;
      if (pwrAbs > 1) exFactor *= 1 + (pwrAbs - 1);
      let exaggerate = pwrAbs * exFactor;
      let diff = (exaggerate - pwrAbs) / 2;
      let percentForPower = meridianPower > 0 ? 1 - percent : percent;
      let power = percentForPower * exaggerate - diff;
      return power;
    },
    powerForLocationRx(xNum, yNum) {
      let angle = parseInt(this.getAngle(xNum, yNum));
      let meridianPower = this.meridianPowers[angle];

      return meridianPower;
    },
    heatmapColor: function (power) {
      if (this.type == "keratometry") return this.keratometryColor(power);
      if (this.type == "NaFl") return this.naflColor(power);
      if (this.type == "rx") return this.rxColor(power);
    },
    keratometryColor: function (power) {
      //   let ratio = (6 - (value - 40)) / 10;
      let h = 120;
      let s = 100;
      let l = 50; // + (43.5 - value) * 2;
      if (power <= 40) h = this.hueForRange(power, 38, 40, 250, 215);
      if (power > 40 && power <= 43)
        h = this.hueForRange(power, 40, 43, 215, 160);
      if (power > 43 && power <= 44)
        h = this.hueForRange(power, 43, 44, 160, 60);
      if (power > 44 && power <= 47)
        h = this.hueForRange(power, 44, 47, 60, 10);
      if (power > 47 && power <= 48) h = this.hueForRange(power, 47, 48, 10, 0);
      if (power > 48) {
        h = this.hueForRange(power, 48, 50, 320, 270);
        l = 70;
      }
      return `background-color: hsl(${parseInt(h)}, ${s}%, ${parseInt(l)}%);`;
    },

    naflColor(power) {
      let l = 30 + power * 30;
      if (l > 75) l = 75;
      return `background-color: hsl(105, 100%, ${parseInt(l)}%);`;
    },
    rxColor(power) {
      let h = 120;
      let s = 100;
      let l = 50; // + (43.5 - value) * 2;
      if (power > 0 && power < 2) {
        h = this.hueForRange(power, 0, 2, 135, 180);
      } else if (power > 2) {
        h = this.hueForRange(power, 2, 15, 180, 280);
      } else if (power > -6.5) {
        h = this.hueForRange(power, -6.5, 0, 0, 135);
      } else if (power <= -6.5) {
        h = this.hueForRange(power, -15, -6.5, 270, 360);
      }

      // if (power <= 40) h = this.hueForRange(power, 6.01, 10, 250, 215);
      // if (power > 40 && power <= 43)
      //   h = this.hueForRange(power, 2.51, 6, 215, 160);
      // if (power > 43 && power <= 44)
      //   h = this.hueForRange(power, -2.5, 2.5, 160, 60);
      // if (power > 44 && power <= 47)
      //   h = this.hueForRange(power, -2.51, -5.5, 60, 10);
      // if (power > 47 && power <= 48)
      //   h = this.hueForRange(power, -6.5, -5.51, 10, 0);
      // if (power > 48) {
      //   h = this.hueForRange(power, -10, -6.51, 320, 270);
      //   l = 70;
      // }
      return `background-color: hsl(${parseInt(h)}, ${s}%, ${parseInt(l)}%);`;
    },
    hueForRange(kVal, kMin, kMax, hueMin, hueMax) {
      let ratio = (kVal - kMin) / (kMax - kMin);
      let hue = ratio * (hueMax - hueMin) + hueMin;
      return hue;
    },
    // powerForMeridian(i, j) {
    //   //   console.log(i, j);
    //   //   return 44;
    //   let meridian = this.getAngle(i, j);
    //   //   console.log(meridian);
    //   if (!this.meridianRxObj) return 0;
    //   let delta = this.powerAtMeridian(this.meridianRxObj, meridian);
    //   return delta + this.flatK(this.meridianRxObj);
    // },
    // calculateMeridianPowers() {},
  },
  computed: {
    elementCount: function () {
      let cnt = Math.floor(this.size / this.resolution);
      // check if odd number
      if (cnt % 2 == 0) cnt++;
      return cnt;
    },
    middleElement: function () {
      return Math.floor(this.elementCount / 2) + 1;
    },
    // for keratometry:
    // make an rx object where the flat meridian is plano and the steep is the cyl
    // eg 42/44@90 = 0.00-2.00x180
    // this will be used to calculate the power at each meridian (and add it to the flat k to get the k power at that meridian)
    meridianRxObj: function () {
      if (this.rxObj) return this.rxObj;
      if (!this.kObj) return null;
      let rx = this.newRxObject();
      rx.sph = 0;
      rx.cyl = this.steepK(this.kObj) - this.flatK(this.kObj);
      rx.axis = this.flatKmeridian(this.kObj);
      return rx;
    },
    // exaggerate cyl by altering the flat k to be flatter and the steep k to be steeper so it shows up better on the map
    modifiedFlatK: function () {
      let flatK = this.kObj ? this.flatK(this.kObj) : 43.5;
      //   console.log("flatK", flatK);
      let steepK = this.kObj ? this.steepK(this.kObj) : 43.5;
      if (steepK > 50) flatK = flatK - (steepK - 50);
      if (flatK < 40) flatK = 40;
      return flatK;
    },
    modifiedMidK: function () {
      let steepK = this.kObj ? this.steepK(this.kObj) : 43.5;
      if (steepK > 50) steepK = 50;
      return this.modifiedFlatK / 2 + steepK / 2;
    },
    rxSphEquiv: function () {
      return this.rxObj ? this.sphericalEquivalent(this.rxObj) : 0;
    },
    meridianPowers: function () {
      let rtn = [];
      if (!this.meridianRxObj) return null;
      let baseline = this.type == "keratometry" ? this.modifiedFlatK : 0;
      for (let i = 0; i <= 180; i++) {
        let delta = this.powerAtMeridian(this.meridianRxObj, i);
        rtn.push(baseline + delta);
      }
      return rtn;
    },
    heightWidthStyle: function () {
      return `height:${this.size}px; width:${this.size}px; `;
    },
    edgeLiftStyle: function () {
      return (
        this.heightWidthStyle +
        `box-shadow: rgba(138, 255, 34, 72) 0px 0px 7px ${
          this.size * (0.05 - this.rxSphEquiv / 50)
        }px inset;`
      );
    },
    ovalHighlightStyle: function () {
      return `width: ${this.size / 1.7}px;
      height: ${this.size / 4}px;
      top: ${this.size / 5}px;
      left: ${this.size / 2}px;`;
    },
    bubbleStyle: function () {
      let sEq = this.rxSphEquiv;
      if (sEq < 2) return "display:none;";
      return `width: ${(this.size / 15) * sEq}px;
        height: ${(this.size / 20) * sEq}px;
        top: ${this.size / 1.9}px;
        left: ${this.size / 1.9}px;`;
    },
    containerDivStyle: function () {
      let rtn = this.heightWidthStyle + "position: absolute; ";
      rtn += `clip-path: circle(${this.size / 2}px at center); `;
      //   if (this.type == "NaFl") rtn += "transform:rotate(90deg); ";
      return rtn;
    },
  },
};
</script>

<style scoped>
.containerDiv {
  filter: blur(8px);
  overflow: hidden;
}
.roundThing {
  position: absolute;
  border-radius: 50%;
}
.innerShadow {
  box-shadow: rgb(0 0 0 / 25%) 0px 0px 32px 8px inset;
  background-image: linear-gradient(0deg, #10517950, transparent, transparent);
}

.outerShadow {
  box-shadow: rgb(1 39 71 / 22%) 0px 5px 11px 2px;
  /* border: 1px solid #9c9c9c; */
}
.ovalHighlight {
  transform: translate(-50%, -50%);
  background-image: linear-gradient(180deg, #ffffffd4, transparent);
  filter: blur(5px);
}
.bubble {
  border-radius: 50%;
  /* background-color: #000; */
  background-image: radial-gradient(#000, #35ba25);
  transform: translate(-50%, -50%);
  position: absolute;
}
</style>
